import { useEffect, useMemo, useState } from "react";
import Decimal from "decimal.js";
import {
  CreateDemandForecastParams,
  DEMAND_FORECAST_CADENCE_DISPLAY_NAMES,
  HydratedDemandForecast,
  Permission,
  UpdateDemandForecastParams,
} from "@forgepoint/shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { CalendarDays, ChevronRight, Loader, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { DemandForecastEditorSheet } from "./DemandForecastEditorSheet";
import { useHasPermission } from "@/store/authStore";
import { useDemandForecastsStore } from "./demandForecastsStore";
import { formatMoney, formatQuantityWithUnit } from "./utils";

interface OpportunityDemandForecastSectionProps {
  opportunityId: string;
  isEditing?: boolean;
  compact?: boolean;
  readOnly?: boolean;
}

function getWeightedRevenue(forecast: HydratedDemandForecast): string {
  const probability = new Decimal(forecast.opportunity?.probability ?? 0).div(
    100,
  );
  return new Decimal(forecast.quantityPerPeriod || 0)
    .mul(forecast.unitPrice || 0)
    .mul(probability)
    .toString();
}

/**
 * Displays an opportunity's demand forecast entries with add/edit/delete controls.
 * @param opportunityId - The ID of the opportunity
 * @param isEditing - Whether the parent opportunity is in edit mode
 * @param compact - Whether to render the section in its compact layout
 * @param readOnly - When true, hides the add/edit/delete affordances
 * @returns The demand forecast section
 */
export function OpportunityDemandForecastSection({
  opportunityId,
  isEditing = false,
  compact = false,
  readOnly = false,
}: OpportunityDemandForecastSectionProps) {
  const {
    demandForecasts,
    demandForecastLoadingStates,
    listLoading,
    fetchDemandForecasts,
    createDemandForecast,
    updateDemandForecast,
    deleteDemandForecast,
  } = useDemandForecastsStore();

  const canEdit = useHasPermission(Permission.DEMANDFORECAST_EDIT);
  const isReadOnly = readOnly || !canEdit;

  const [forecastIds, setForecastIds] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(compact);
  const [isCreateSheetOpen, setIsCreateSheetOpen] = useState(false);
  const [editingForecastId, setEditingForecastId] = useState<string | null>(
    null,
  );

  const refresh = async () => {
    const result = await fetchDemandForecasts({
      opportunityId,
      limit: 50,
      offset: 0,
    });
    setForecastIds(result.ids);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opportunityId]);

  const forecasts = useMemo(
    () =>
      forecastIds
        .map((id) => demandForecasts.get(id))
        .filter((forecast): forecast is HydratedDemandForecast => !!forecast),
    [demandForecasts, forecastIds],
  );

  const weightedTotal = forecasts.reduce(
    (total, forecast) => total.add(getWeightedRevenue(forecast)),
    new Decimal(0),
  );

  if (isEditing) {
    return null;
  }

  const editingForecast = editingForecastId
    ? demandForecasts.get(editingForecastId)
    : undefined;

  return (
    <div className={compact ? "" : "border-t pt-6"}>
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <div
          className={cn(
            "flex items-center justify-between",
            compact ? "mb-3" : "mb-4",
          )}
        >
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="group flex cursor-pointer items-center gap-2"
            >
              <ChevronRight
                className={cn(
                  "h-4 w-4 text-muted-foreground transition-transform duration-200",
                  isOpen && "rotate-90",
                )}
              />
              <h3
                className={cn("font-semibold", compact ? "text-sm" : "text-lg")}
              >
                Demand Forecast
              </h3>
              {!listLoading && forecasts.length > 0 && (
                <Badge variant="secondary" className="text-xs tabular-nums">
                  {forecasts.length}
                </Badge>
              )}
            </button>
          </CollapsibleTrigger>
          {!isReadOnly && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsCreateSheetOpen(true)}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Forecast
            </Button>
          )}
        </div>

        <CollapsibleContent>
          {listLoading && forecasts.length === 0 ? (
            <div className="space-y-1.5">
              {[0, 1].map((index) => (
                <Skeleton key={index} className="h-16 w-full" />
              ))}
            </div>
          ) : forecasts.length === 0 ? (
            <div
              className={cn(
                "flex flex-col items-center justify-center rounded-lg border px-4 text-center",
                compact ? "py-6" : "py-8",
              )}
            >
              <CalendarDays className="mb-2 h-7 w-7 text-muted-foreground/40" />
              <p className="text-sm text-muted-foreground">
                No forecast entries yet
              </p>
            </div>
          ) : (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between rounded-lg border bg-muted/40 px-3 py-2 text-sm">
                <span className="text-muted-foreground">
                  Weighted revenue per first period
                </span>
                <span className="font-medium tabular-nums">
                  {formatMoney(weightedTotal)}
                </span>
              </div>
              {forecasts.map((forecast) => {
                const isDeleting =
                  demandForecastLoadingStates.get(forecast.id)
                    ?.isLoadingDelete ?? false;
                return (
                  <div
                    key={forecast.id}
                    role={isReadOnly ? undefined : "button"}
                    tabIndex={isReadOnly ? undefined : 0}
                    onClick={
                      isReadOnly
                        ? undefined
                        : () => setEditingForecastId(forecast.id)
                    }
                    onKeyDown={
                      isReadOnly
                        ? undefined
                        : (event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              setEditingForecastId(forecast.id);
                            }
                          }
                    }
                    className={cn(
                      "w-full rounded-lg border p-3 text-left transition-colors",
                      !isReadOnly && "hover:bg-muted/50",
                    )}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">
                          {forecast.description}
                        </div>
                        {forecast.reasoning && (
                          <div className="mt-1 line-clamp-2 text-xs italic text-muted-foreground/80">
                            {forecast.reasoning}
                          </div>
                        )}
                        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                          <span>
                            {formatQuantityWithUnit(
                              forecast.quantityPerPeriod,
                              forecast.quantityUnit,
                            )}
                          </span>
                          <span>{formatMoney(forecast.unitPrice)} / unit</span>
                          <span>
                            {
                              DEMAND_FORECAST_CADENCE_DISPLAY_NAMES[
                                forecast.cadence
                              ]
                            }
                          </span>
                          {forecast.location?.name && (
                            <span>{forecast.location.name}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <div className="text-right text-sm font-medium tabular-nums">
                          {formatMoney(getWeightedRevenue(forecast))}
                        </div>
                        {!isReadOnly && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async (event) => {
                              event.stopPropagation();
                              const result = await deleteDemandForecast(
                                forecast.id,
                              );
                              if (result.success) {
                                toast.success("Forecast deleted");
                                await refresh();
                              } else {
                                toast.error(
                                  result.error ?? "Failed to delete forecast",
                                );
                              }
                            }}
                            disabled={isDeleting}
                          >
                            {isDeleting ? (
                              <Loader className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CollapsibleContent>
      </Collapsible>

      <DemandForecastEditorSheet
        open={isCreateSheetOpen}
        onOpenChange={setIsCreateSheetOpen}
        mode="create"
        fixedOpportunityId={opportunityId}
        onSave={async (data: CreateDemandForecastParams) => {
          const { demandForecast, error } = await createDemandForecast(data);
          if (demandForecast) {
            await refresh();
            return true;
          }
          return error ?? "Failed to create forecast";
        }}
      />

      {editingForecast && (
        <DemandForecastEditorSheet
          open={!!editingForecastId}
          onOpenChange={(open) => !open && setEditingForecastId(null)}
          mode="edit"
          fixedOpportunityId={opportunityId}
          demandForecast={editingForecast}
          readOnly={isReadOnly}
          onSave={async (data: UpdateDemandForecastParams) => {
            const { demandForecast, error } = await updateDemandForecast(
              editingForecast.id,
              data,
            );
            if (demandForecast) {
              await refresh();
              return true;
            }
            return error ?? "Failed to update forecast";
          }}
        />
      )}
    </div>
  );
}
